<template>
  <div>
    <p class="desc pm-txt-color">
      All we need now is to confirm your settlement date and the details of your solicitor/conveyancer (if
      applicable).<br />
      Your solicitor/conveyancer will act as a witness for any electronic signing required and will assist you through
      loan settlement.
    </p>

    <div class="icon-wrapper p-d-flex p-ai-center">
      <img :src="require('@/assets/icons/loan_summ_icon.svg')" alt="" />
      <p class="heading pm-txt-color">Loan Summary</p>
    </div>
    <div class="section-1">
      <div class="grid-items">
        <div class="wrap">
          <p class="heading lt-txt-color">Loan</p>
          <p class="text pm-txt-color" v-if="selectedLoanDetails">
            {{ formatPurpose(selectedLoanDetails.loanReferenceNumber) }}
          </p>
        </div>
        <div v-if="selectedLoanDetails?.calculatedValue?.interestFreeTermMonths > 0" class="wrap">
          <p class="heading lt-txt-color">Interest free period</p>
          <p class="text pm-txt-color" v-if="selectedLoanDetails">
            {{ selectedLoanDetails?.calculatedValue?.interestFreeTermMonths }}
            months
          </p>
        </div>
        <div v-else class="wrap"></div>
        <div class="wrap">
          <p class="heading lt-txt-color">Term</p>
          <p class="text pm-txt-color" v-if="selectedLoanDetails?.calculatedValue?.loanTermMonths">
            {{ loanTermDisplay }}
          </p>
        </div>
        <div v-if="selectedLoanDetails?.calculatedValue?.interestFreeTermMonths > 0" class="wrap">
          <p class="heading lt-txt-color">Interest rate</p>
          <p class="text pm-txt-color" v-if="selectedLoanDetails?.calculatedValue">
            {{ (interestRate * 100).toFixed(2) }}% after interest free period
          </p>
        </div>
        <div v-else class="wrap">
          <p class="heading lt-txt-color">Interest rate</p>
          <p class="text pm-txt-color" v-if="selectedLoanDetails?.calculatedValue">
            {{ (interestRate * 100).toFixed(2) }}%
          </p>
        </div>
        <div class="wrap">
          <p class="heading lt-txt-color">Valuation Fee</p>
          <p class="text pm-txt-color">{{ formatCurrency(valuationCost) }}</p>
        </div>
        <div class="wrap">
          <p class="heading lt-txt-color">{{ setupFeeLabel }}</p>
          <p class="text pm-txt-color">{{ (setupFeePercentage * 100).toFixed(2) }}%</p>
        </div>
        <div class="wrap">
          <p class="heading lt-txt-color">Loan Deposit</p>
          <p class="text pm-txt-color">{{ formatCurrency(loanDeposit) }}</p>
        </div>
        <div class="wrap">
          <p class="heading lt-txt-color">Loan Amount</p>
          <p class="text primary-color">{{ formatCurrency(loanAmount) }}</p>
        </div>
        <template v-if="isErhl">
          <div class="wrap">
            <p class="heading lt-txt-color">Scheduled Equity Release Term</p>
            <p class="text pm-txt-color">{{ formatErhlYears(equityReleaseTermYears) }}</p>
          </div>
          <div class="wrap">
            <p class="heading lt-txt-color">Monthly Scheduled Equity Release</p>
            <p class="text pm-txt-color">{{ formatErhlCurrency(monthlyEquityReleaseAmount) }}</p>
          </div>
          <div class="wrap" v-if="!isPurchase">
            <p class="heading lt-txt-color">Lump Sum Requested</p>
            <p class="text pm-txt-color">{{ formatErhlCurrency(lumpSumRequested) }}</p>
          </div>
          <div class="wrap" v-if="!isPurchase">
            <p class="heading lt-txt-color">Mortgage payout estimate</p>
            <p class="text pm-txt-color">{{ formatErhlCurrency(mortgagePayoutEstimate) }}</p>
          </div>
        </template>
      </div>
    </div>
    <!-- Only a non-purchase application asks the question at all. A purchase always conveys, so its
         solicitor details stay mandatory and are shown outright, exactly as before. -->
    <div class="section-solicitor-toggle" v-if="!isPurchase">
      <p class="sec-heading pm-txt-color font-size-1">
        Is your solicitor/conveyancer involved in this transaction? (Optional)
      </p>
      <Checkbox
        id="solicitor-involved"
        label="Yes"
        :value="formData.solicitorInvolved"
        @getInputValue="updateField('solicitorInvolved', $event)"
      />
    </div>
    <div class="section-2" v-if="showSolicitorDetails">
      <p class="sec-heading pm-txt-color font-size-1">Solicitor/conveyancer details</p>
      <div class="inputs">
        <TextField
          width="100%"
          placeholder="First name"
          :value="formData.firstName"
          label="First Name"
          :errorMessages="errors.firstName"
          @onInput="updateField('firstName', $event)"
        />
        <TextField
          width="100%"
          placeholder="Last name"
          label="Last Name"
          :value="formData.lastName"
          :errorMessages="errors.lastName"
          @onInput="updateField('lastName', $event)"
        />
        <TextField
          width="100%"
          placeholder="Email address"
          label="Email"
          :value="formData.email"
          :errorMessages="errors.email"
          @onInput="updateField('email', $event)"
        />
      </div>

      <div class="mt-3 inputs">
        <TextField
          width="100%"
          placeholder="Phone number"
          label="Phone"
          type="tel"
          maxLength="10"
          :value="formData.mobilePhoneNumber"
          :errorMessages="errors.mobilePhoneNumber"
          @onInput="updateField('mobilePhoneNumber', $event)"
        />
        <TextField
          width="100%"
          placeholder="Company"
          label="Company"
          :value="formData.company"
          :errorMessages="errors.company"
          @onInput="updateField('company', $event)"
        />
        <InputAddress :value="formData.address" @onAddressChange="onAddressChange" :errorMessages="errors.address" />
      </div>
    </div>
    <div class="section-3">
      <label class="hk-grotest-medium mb-1">{{ settlementDateLabel }}</label>
      <div class="inputs">
        <DatePicker
          :minDate="currentDate()"
          yearRange="2022:2050"
          :value="formData.settlementDate"
          :disabled="true"
          @change="updateField('settlementDate', $event)"
        />
      </div>
      <label v-if="errors.settlementDate" class="error-text">{{ errors.settlementDate }}</label>
    </div>
  </div>
</template>

<script>
import { currentDate } from '@/services/util';
import { formatCurrency, formatTermFromMonths, yearsFormatter } from '@/services/format';
import Checkbox from '@/components/common/Checkbox.vue';
import InputAddress from '@/components/common/InputAddress.vue';

export default {
  components: { Checkbox, InputAddress },
  props: {
    selectedLoanDetails: { type: Object, required: true },
    formData: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    isErhl: { type: Boolean, default: false },
    settlementDateLabel: { type: String, default: 'Settlement Date' },
    interestRate: { type: Number, default: 0 },
    setupFeeLabel: { type: String, default: 'Total set-up fee (%)' },
    setupFeePercentage: { type: Number, default: 0 },
    valuationCost: { type: Number, default: 0 },
    loanDeposit: { type: Number, default: 0 },
    loanAmount: { type: Number, default: 0 },
    equityReleaseTermYears: { type: Number, default: 0 },
    monthlyEquityReleaseAmount: { type: Number, default: 0 },
    lumpSumRequested: { type: Number, default: 0 },
    isPurchase: { type: Boolean, default: false },
    mortgagePayoutEstimate: { type: Number, default: 0 },
    equityReleaseEndDate: { type: String, default: '' },
    formatErhlCurrency: { type: Function, required: true },
    formatErhlYears: { type: Function, required: true },
    formatErhlDate: { type: Function, required: true },
  },
  computed: {
    // A purchase always conveys, so its details stay mandatory. A non-purchase reveals them only
    // once the optional tick box is checked.
    showSolicitorDetails() {
      return this.isPurchase || Boolean(this.formData.solicitorInvolved);
    },
    loanTermDisplay() {
      const loanTermMonths = this.selectedLoanDetails?.calculatedValue?.loanTermMonths;
      if (!loanTermMonths) return '';
      if (this.isErhl) return formatTermFromMonths(loanTermMonths);
      return `${loanTermMonths} months`;
    },
  },
  methods: {
    currentDate,
    formatCurrency,
    yearsFormatter,
    formatPurpose(purpose) {
      const obj = {
        buy_now: 'Buy now',
        renovate: 'Renovate',
        cash_advance: 'Equity release',
        equity_release: 'Equity release',
      };
      return obj[purpose] ? obj[purpose] : purpose;
    },
    updateField(fieldName, value) {
      this.$emit('update-form', { ...this.formData, [fieldName]: value });
    },
    onAddressChange(address) {
      this.$emit('update-form', { ...this.formData, address });
    },
  },
};
</script>

<style lang="scss" scoped>
.desc {
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  line-height: 2;
}

.heading,
.text {
  font-size: 0.875rem !important;
  margin: 0;
}

.icon-wrapper {
  gap: 1rem;
}

.heading {
  margin: 0;
  font-weight: bold;
  font-size: 1.25rem;
}

.section-1 {
  margin-top: 2rem;
  width: 90%;

  .grid-items {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
    align-items: start;
    @media (max-width: 992px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

.section-solicitor-toggle {
  margin-top: 2rem;
}

.section-2 {
  margin-top: 1.875rem;
}

.section-3 {
  margin-top: 1.875rem;
}

.inputs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  align-items: start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
}

.error-text {
  color: red;
  font-size: 14px;
}
</style>
